import { useEffect, useState } from 'react';
import { Banner } from '@packages/ui/layout';
import { Typography } from '@packages/ui/primitives';
import Button from '../../../../components/button';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';

interface AdvisorApiKeyRevealContentProps {
  apiKey: string;
  apiKeyPrefix?: string | null;
}

const AdvisorApiKeyRevealContent = ({
  apiKey,
  apiKeyPrefix,
}: AdvisorApiKeyRevealContentProps): JSX.Element => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    setIsCopied(false);
  }, [apiKey]);

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(apiKey).then(() => {
      setIsCopied(true);
    });
  };

  return (
    <div className='flex flex-col gap-4'>
      <Banner variant='warning'>
        <Typography variant='body-s'>
          This is the only time the full API key is shown. We store only a secure hash, so the plain
          key cannot be recovered later. Copy it now and keep it in a secure location such as a
          password manager.
        </Typography>
      </Banner>

      {apiKeyPrefix && (
        <Heading variant='body-xs' color={COLORS.SpanishGray} title={`Prefix: ${apiKeyPrefix}`} />
      )}

      <div
        className='flex items-stretch w-full rounded-md px-3 py-3 bg-zinc-100'
        style={{
          border: '1px solid #E4E4E7',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}>
        <Heading variant='h6' color={COLORS.Purple} title={apiKey} />
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <Button
          title={isCopied ? 'Copied' : 'Copy API Key'}
          variant={isCopied ? 'primary-outline' : 'primary'}
          icon={isCopied ? 'check_circle' : 'content_copy'}
          onClick={handleCopy}
          id='copyAdvisorApiKeyButton'
        />
        {isCopied && (
          <Heading
            variant='body-xs'
            color={COLORS.Green}
            title='Key copied. You can close this dialog once you have stored it safely.'
          />
        )}
      </div>
    </div>
  );
};

export default AdvisorApiKeyRevealContent;
